import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { 
    Box, Typography, CircularProgress, Tabs, Tab,
    FormControl, InputLabel, Select, MenuItem, IconButton,
    Dialog, DialogTitle, DialogContent, Tooltip
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { axios_tenant } from "../../../axios";
import TaskTable from "../../shared/OAWorkflow/components/TaskTable";
import OAPagination from "../../shared/OAWorkflow/components/OAPagination";
import SubmitRequestView from "../../shared/OAWorkflow/components/SubmitRequestView";
import OASearchBar from "../../shared/OAWorkflow/components/OASearchBar";
import { CLIENT_COMPLETED_COLUMNS, BRAND_COLORS, TAB_STYLES } from "../../shared/OAWorkflow/constants/taskStatuses";
import { formatDateDDMMMYYYY } from "../../shared/OAWorkflow/utils/dateUtils";
import { useDocumentTitle } from "../../shared/OAWorkflow/utils/useDocumentTitle";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";

const BASE_URL = "/collaboration/workflow";

// Client-view columns for Post Grant Support
const PGS_CLIENT_COLUMNS = [
    { id: "applicationNo", label: "Application No.", minWidth: 140 },
    { id: "patentNo", label: "Patent No.", minWidth: 140, renderCell: (task) => task.extraFields?.patentNo || "—" },
    { id: "supportType", label: "Support Type", minWidth: 130 },
    { id: "activityName", label: "Activity", minWidth: 200 },
    { id: "taskStatus", label: "Status", minWidth: 120 },
    { id: "routedClient", label: "Client", minWidth: 140 },
    { id: "requestedDate", label: "Requested Date", minWidth: 130 },
    { id: "package", label: "Package", minWidth: 100 }
];

const OA_SHELL_SEARCH_FIELDS = [
    { value: "all", label: "All" },
    { value: "applicationNo", label: "Application Number" },
    { value: "clientName", label: "Client Name" },
    { value: "docketNo", label: "Docket No." },
    { value: "activityName", label: "Activity Name" },
    { value: "oaMailDate", label: "Received Date" },
    { value: "attorneyInitials", label: "Attorney Initials" }
];

const PGS_SEARCH_FIELDS = [
    { value: "all", label: "All" },
    { value: "applicationNo", label: "Application No." },
    { value: "supportType", label: "Support Type" },
    { value: "activityName", label: "Activity" },
    { value: "taskStatus", label: "Status" },
    { value: "routedClient", label: "Client" },
    { value: "requestedDate", label: "Requested Date" }
];

/**
 * Client/User Task Manager — read-only listing with Completed/Pending tabs.
 * Reuses the shared TaskTable component with client-specific columns.
 */
const OAWorkflowClient = ({ workType = "OA Shell" }) => {
    useDocumentTitle(workType === "Post Grant Support" ? "Post Grant Support" : "OA Shell");
    const outerTheme = useTheme();
    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: { fontFamily: 'Lato, sans-serif' },
    }), [outerTheme]);

    const [searchParams] = useSearchParams();
    const appNoParam = searchParams.get("application_no");

    const [tasks, setTasks] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);

    // Filters & Modal States
    const [mainTab, setMainTab] = useState(0); // COL-144: 0 = Completed, 1 = Pending
    const [groupBy, setGroupBy] = useState("None");
    const [submitModalOpen, setSubmitModalOpen] = useState(false);

    // Search state (using OASearchBar)
    const [searchVal, setSearchVal] = useState(appNoParam || "");
    const [searchField, setSearchField] = useState("all");

    // Server-side sorting state — default to Requested Date (PGS) / Received
    // Date (OA Shell) per QA's requirement discussion.
    const [sortBy, setSortBy] = useState(workType === "Post Grant Support" ? "requestedDate" : "oaMailDate");
    const [sortOrder, setSortOrder] = useState(workType === "Post Grant Support" ? "desc" : "asc");

    // Group-by pagination and expansion states
    const [expandedGroups, setExpandedGroups] = useState({});
    const [groupPages, setGroupPages] = useState({});
    const [groupPageSizes, setGroupPageSizes] = useState({});
    const [groupPage, setGroupPage] = useState(0);
    const [groupsPerPage, setGroupsPerPage] = useState(10);

    // Reset group states when groupBy filter changes
    useEffect(() => {
        setExpandedGroups({});
        setGroupPages({});
        setGroupPageSizes({});
        setGroupPage(0);
    }, [groupBy]);

    const fetchTasks = useCallback(async (signal) => {
        try {
            setLoading(true);
            const res = await axios_tenant.get(`${BASE_URL}/tasks`, {
                params: {
                    page: groupBy !== "None" ? 1 : page + 1,
                    limit: groupBy !== "None" ? 10000 : rowsPerPage,
                    workType,
                    // COL-144: tab drives the status filter. "Pending" maps to
                    // taskStatus != Completed on the backend.
                    status: mainTab === 0 ? "Completed" : "Pending",
                    sortBy,
                    sortOrder,
                    ...(searchField === "all"
                        ? (searchVal && { search: searchVal })
                        : { filterField: searchField, filterValue: searchVal })
                },
                signal,
                silent: true
            });
            setTasks(res.data.data || []);
            setTotalRecords(res.data.pagination?.totalRecords || 0);
        } catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') return;
            console.error("Failed to fetch tasks:", err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, workType, mainTab, searchVal, searchField, sortBy, sortOrder, groupBy]);

    useEffect(() => {
        const controller = new AbortController();
        fetchTasks(controller.signal);
        return () => controller.abort();
    }, [fetchTasks, workType]);

    // Resolve raw client codes on tasks to friendly tenant names for the
    // Client/Client Name column, same lookup as the internal task lists.
    useEffect(() => {
        axios_tenant.get(`${BASE_URL}/intake/clients`, { params: { workType }, silent: true })
            .then(res => setClients(res.data.clients || []))
            .catch(err => console.error("Failed to fetch clients:", err));
    }, [workType]);

    const handleSearch = (value, field) => {
        setSearchVal(value);
        setSearchField(field);
        setPage(0);
    };

    const handleRefresh = () => {
        fetchTasks();
    };

    const handleOpenSubmitModal = () => {
        setSubmitModalOpen(true);
    };

    const handleCloseSubmitModal = () => {
        setSubmitModalOpen(false);
    };

    const handleSubmitSuccess = () => {
        setSubmitModalOpen(false);
        fetchTasks();
    };

    // Server-side sort handler
    const handleSortChange = (columnId) => {
        if (sortBy === columnId) {
            setSortOrder(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortBy(columnId);
            setSortOrder("asc");
        }
        setPage(0);
    };

    // Determine target columns based on workType, resolving the raw
    // client code column to the friendly tenant name once `clients` loads.
    const clientColumnId = workType === "Post Grant Support" ? "routedClient" : "clientName";
    const columns = React.useMemo(() => {
        const base = workType === "Post Grant Support" ? PGS_CLIENT_COLUMNS : CLIENT_COMPLETED_COLUMNS;
        return base.map(col => col.id !== clientColumnId ? col : {
            ...col,
            renderCell: (task) => {
                const rawClient = task.clientName || task.clientType || task.routedClient || task.tenantCode || "—";
                const foundClient = clients.find(c => c.tenantCode === rawClient || c.code === rawClient);
                return foundClient ? foundClient.tenantName : rawClient;
            }
        });
    }, [workType, clientColumnId, clients]);

    // Frontend group-by computation
    const groupedTasksMap = React.useMemo(() => {
        if (!groupBy || groupBy === "None") return null;
        const groups = {};
        tasks.forEach(task => {
            let key = "—";
            if (groupBy === "Client") {
                key = task.clientName || task.routedClient || "—";
            } else if (groupBy === "Activity") {
                key = task.activityName || "—";
            } else if (groupBy === "Attorney Initials") {
                key = task.attorneyInitials || "—";
            } else if (groupBy === "Package Status") {
                key = task.packageStatus || "—";
            } else if (groupBy === "Status") {
                key = task.taskStatus || "—";
            } else if (groupBy === "Application Number") {
                key = task.applicationNo || "—";
            } else if (groupBy === "Requested Date") {
                key = formatDateDDMMMYYYY(task.requestedDate);
            }
            if (!groups[key]) groups[key] = [];
            groups[key].push(task);
        });
        return groups;
    }, [tasks, groupBy]);

    // Group By Options
    const groupByOptions = workType === "Post Grant Support"
        ? [
            { value: "None", label: "None" },
            { value: "Client", label: "Client" },
            { value: "Activity", label: "Activity" },
            { value: "Status", label: "Status" },
            { value: "Application Number", label: "Application Number" },
            { value: "Requested Date", label: "Requested Date" }
          ]
        : [
            { value: "None", label: "None" },
            { value: "Client", label: "Client" },
            { value: "Activity", label: "Activity" },
            { value: "Application Number", label: "Application Number" },
            { value: "Attorney Initials", label: "Attorney Initials" },
            { value: "Package Status", label: "Package Status" }
          ];

    const pendingStatusOptions = React.useMemo(() => [
        { value: "",               label: "Select Status..." },
        { value: "To Do",          label: "To Do" },
        { value: "In Progress",    label: "In Progress" },
        { value: "Assign QA",      label: "Assign QA" },
        { value: "QC Pending",     label: "QC Pending" },
        { value: "Under Review",   label: "Under Review" }
    ], []);

    const searchFieldsConfig = React.useMemo(() => {
        const baseFields = workType === "Post Grant Support" ? PGS_SEARCH_FIELDS : OA_SHELL_SEARCH_FIELDS;
        if (mainTab === 0) {
            return baseFields.filter(f => f.value !== "taskStatus");
        }
        return baseFields;
    }, [workType, mainTab]);

    return (
        <ThemeProvider theme={localTheme}>
            <Box
                className="oa-lato-font"
                sx={{ height: "calc(100vh - 150px)", display: "flex", flexDirection: "column", overflow: "hidden", pt: 0, mt: -3, px: 4, pb: 1 }}
                data-testid="oa-client-task-manager-page"
            >
                {/* Top Row: Title */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography
                        variant="h5"
                        sx={{ color: "#1E293B", fontSize: "20px", fontWeight: 600 }}
                    >
                        {workType === "Post Grant Support" ? "Post Grant Support" : "OA Shell"}
                    </Typography>
                </Box>

                {/* COL-144: Pending / Completed tabs (replaces the status dropdown) */}
                <Tabs
                    value={mainTab}
                    onChange={(e, v) => {
                        setMainTab(v);
                        setPage(0);
                        if (searchField === "taskStatus") {
                            setSearchVal("");
                            setSearchField("all");
                        }
                    }}
                    sx={{ ...TAB_STYLES, mb: 2, minHeight: 40, "& .MuiTab-root": { textTransform: "none", minHeight: 40, fontWeight: 600 } }}
                >
                    <Tab label="Completed" />
                    <Tab label="Pending" />
                </Tabs>

                {/* Filters Row */}
                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
                    {/* Left: Search input */}
                    <OASearchBar
                        onSearch={handleSearch}
                        fields={searchFieldsConfig}
                        placeholder={searchField === "all" ? "Search Application Number..." : undefined}
                        statusOptions={mainTab === 1 ? pendingStatusOptions : undefined}
                    />

                    {/* Right: Dropdowns, Refresh, and Add Button */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {/* Group By Filter */}
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="group-by-label">Group By</InputLabel>
                            <Select
                                labelId="group-by-label"
                                value={groupBy}
                                label="Group By"
                                onChange={(e) => setGroupBy(e.target.value)}
                                sx={{ backgroundColor: "#fff" }}
                            >
                                {groupByOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Refresh Action */}
                        <Tooltip title="Refresh Table">
                            <IconButton
                                onClick={handleRefresh}
                                sx={{
                                    border: `1px solid ${BRAND_COLORS.PRIMARY}`, borderRadius: "6px",
                                    backgroundColor: "#fff", height: 38, width: 38,
                                    color: BRAND_COLORS.PRIMARY,
                                    "&:hover": { backgroundColor: BRAND_COLORS.SECONDARY_HOVER }
                                }}
                            >
                                <SyncIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>

                        {/* Square blue Plus Icon Button */}
                        <Tooltip title="Submit Request">
                            <IconButton
                                onClick={handleOpenSubmitModal}
                                sx={{
                                    backgroundColor: BRAND_COLORS.PRIMARY,
                                    color: "#fff",
                                    borderRadius: "6px",
                                    width: 38,
                                    height: 38,
                                    "&:hover": {
                                        backgroundColor: BRAND_COLORS.PRIMARY_HOVER
                                    }
                                }}
                            >
                                <AddIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Main Content Area */}
                <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    {loading && (
                        <Box sx={{
                            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: "rgba(255,255,255,0.6)",
                            zIndex: 10, display: "flex", justifyContent: "center", alignItems: "center",
                            minHeight: 200
                        }}>
                            <CircularProgress size={32} />
                        </Box>
                    )}

                    {/* Render Grouped Table or Flat Table */}
                    {groupBy !== "None" && groupedTasksMap ? (
                        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, overflowY: "auto", pr: 1 }}>
                                {Object.entries(groupedTasksMap)
                                    .slice(groupPage * groupsPerPage, (groupPage + 1) * groupsPerPage)
                                    .map(([groupName, groupTasks]) => {
                                        const isExpanded = !!expandedGroups[groupName];
                                        const currentPage = groupPages[groupName] || 0;
                                        const pageSize = groupPageSizes[groupName] || 10;
                                        const paginatedTasks = groupTasks.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

                                        return (
                                            <Box key={groupName} sx={{ display: "flex", flexDirection: "column" }}>
                                                <Box
                                                    onClick={() => setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }))}
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        fontWeight: 600,
                                                        fontSize: "14px",
                                                        color: "#475569",
                                                        backgroundColor: "#F1F5F9",
                                                        px: 2,
                                                        py: 1.5,
                                                        borderRadius: isExpanded ? "6px 6px 0 0" : "6px",
                                                        border: "1px solid #E2E8F0",
                                                        cursor: "pointer",
                                                        userSelect: "none",
                                                        "&:hover": {
                                                            backgroundColor: "#E2E8F0"
                                                        }
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#475569" }}>
                                                        {groupBy}: {groupName} ({groupTasks.length} {groupTasks.length === 1 ? "task" : "tasks"})
                                                    </Typography>
                                                    {isExpanded ? <ExpandLessIcon sx={{ fontSize: 20, color: "#64748B" }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: "#64748B" }} />}
                                                </Box>
                                                {isExpanded && (
                                                    <TaskTable
                                                        tasks={paginatedTasks}
                                                        totalRecords={groupTasks.length}
                                                        page={currentPage}
                                                        rowsPerPage={pageSize}
                                                        columns={columns}
                                                        enableLink={false}
                                                        selectable={false}
                                                        sortBy={sortBy}
                                                        sortOrder={sortOrder}
                                                        onSortChange={handleSortChange}
                                                        onPageChange={(newPage) => setGroupPages(prev => ({ ...prev, [groupName]: newPage }))}
                                                        onRowsPerPageChange={(newSize) => {
                                                            setGroupPageSizes(prev => ({ ...prev, [groupName]: newSize }));
                                                            setGroupPages(prev => ({ ...prev, [groupName]: 0 }));
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })
                                }
                                {tasks.length === 0 && !loading && (
                                    <Box sx={{ textAlign: "center", py: 6, border: "1px dashed #CBD5E1", borderRadius: 2 }}>
                                        <Typography color="text.secondary">No tasks found for your selection</Typography>
                                    </Box>
                                )}
                            </Box>
                            {Object.keys(groupedTasksMap).length > groupsPerPage && (
                                <OAPagination
                                    count={Object.keys(groupedTasksMap).length}
                                    page={groupPage}
                                    rowsPerPage={groupsPerPage}
                                    onPageChange={(newPage) => setGroupPage(newPage)}
                                    onRowsPerPageChange={(newSize) => {
                                        setGroupsPerPage(newSize);
                                        setGroupPage(0);
                                    }}
                                    label="Groups per page:"
                                />
                            )}
                        </Box>
                    ) : (
                        <TaskTable
                            tasks={tasks}
                            totalRecords={totalRecords}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            loading={loading}
                            onPageChange={setPage}
                            onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(0); }}
                            columns={columns}
                            enableLink={false}
                            selectable={false}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSortChange={handleSortChange}
                        />
                    )}
                </Box>

                {/* Submit Request Modal Dialog */}
                <Dialog
                    open={submitModalOpen}
                    onClose={handleCloseSubmitModal}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: "12px", p: 1 }
                    }}
                >
                    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ color: "#0F172A" }}>
                            New Request — {workType === "Post Grant Support" ? "Post Grant Support" : "OA Shell"}
                        </Typography>
                        <IconButton onClick={handleCloseSubmitModal} size="small" sx={{ color: "#64748B" }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <SubmitRequestView 
                            workType={workType} 
                            onSuccess={handleSubmitSuccess} 
                        />
                    </DialogContent>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
};

export default OAWorkflowClient;
